/**
 * DASHBOARD-RESULTS.WIDGET
 * Display tree, podium and axis
 */

import { withTranslation, WithTranslation } from "react-i18next"
import { connect } from "react-redux"
import Card from "@/components/card"
import Tooltip from "@/components/tooltip"
import { useContext, useEffect, useState } from "react"
import { HelpGuideContext } from "@/components/help-guide.context"
import { HELP_COPY_BUTTON_WIDTH } from "@/components/help-hint"
import { hasHelpSection } from "@/utils/help-article.utils"
import { DashboardFilters } from "@/redux/filter.types"
import { Topic } from "@/redux/topic.types"
import { store } from "@/index"
import { fetchAxes, fetchNote, fetchPropositions, fetchTopics } from "@/redux/_archive.actions"
import { Survey } from "@/redux/survey.types"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faChartPie, faChevronRight, faDotCircle } from "@fortawesome/free-solid-svg-icons"
import { Session } from "@/redux/_session.types"
import { INSUFFICIENT_PARTICIPATION, STATUS_LOADED, STATUS_LOADING, STATUS_LOAD_ERROR } from "@/redux/_status.types"
import { flatten, mean, orderBy, sortBy, uniqBy } from "lodash"
import { Axis } from "@/redux/axis.types"
import Space from "@/components/space"
import NoResultsWidget from "./no-results.widget"
import { generateTopics } from "@/redux/_generate.actions"
import { questionGet } from "@/redux/question.actions"
import { Question, QuestionState } from "@/redux/question.types"
import ListItem from "@/components/list-item"
import getArchiveTopic from "@/utils/get-archive-topic.utils"
import formatAxisResults from "@/utils/format-axis-results.utils"
import DashboardResultsTopicsWidget from "./dashboard-results-topics-animated.widget"
import DashboardResultsTopicsSkeletonWidget from "./skeletons/dashboard-results-topics-skeleton.widget"
import DashboardResultsAxesWidget from "./dashboard-results-axes-animated.widget"
import DashboardTopicsPodiumWidget from "./dashboard-topics-podium.widget"
import DashboardAxesOverviewWidget from "./dashboard-axes-overview.widget"
import { NavigateFunction, useNavigate } from "react-router-dom"
import { axisFindTopicAid } from "@/redux/axis.actions"

interface StateProps extends WithTranslation {
  _session: Session
  question: QuestionState
}

interface OwnProps {
  exportName: string
  currentAxes: Axis[]
  currentSurvey: Survey
  currentTopics: Topic[]
  dashboardFilters: DashboardFilters
  isPreview?: boolean
  isSelf?: boolean
  isSortByName?: boolean
  isReport?: boolean
}

type Props = StateProps & OwnProps

const DISPLAY_TREE_CEIL: number = 3 //Limit of topics with a note to display tree
const WIDGET_WIDTH: number = 420

type DisplayMode = "topics" | "axes"

const modes = [
  { name: "topics", icon: faChartPie, label: "dashboard_topics" },
  { name: "axes", icon: faDotCircle, label: "dashboard_axes" }
]

function DashboardResultsWidget(props: Props) {
  const helpGuide = useContext(HelpGuideContext)
  const hasHelpHint = helpGuide.isAvailable && hasHelpSection(helpGuide.article, "rosace")
  const { t } = props
  const navigate: NavigateFunction = useNavigate()

  //Init state
  const [axes, setAxes] = useState<Axis[]>([])
  const [topics, setTopics] = useState<Topic[]>([])
  const [status, setStatus] = useState(STATUS_LOADING)

  const [mode, setMode] = useState<DisplayMode>("topics")

  //Note to display at the center of the circle
  const [note, setNote] = useState(0)

  const [selectedAxis, setSelectedAxis] = useState<Axis | null>(null)

  //On every change on the dashboard filters, reload topics
  useEffect(() => {
    //Une réponse qui arrive après un changement de filtres ne doit pas écraser les résultats plus récents
    let isCancelled = false

    async function load() {
      setStatus(STATUS_LOADING)

      //Load topics
      let response: any = []

      //La note et les axes ne dépendent pas de la réponse des thèmes : demandés en même temps pour ne pas attendre
      //trois requêtes l'une après l'autre
      let noteRequest: any = null
      let axesRequest: any = null

      //GenerateData = true
      if (props.currentSurvey.randomData) {
        //Generate random data
        //If survey is test (not demo), load data from a surveyId
        response = await store.dispatch(
          generateTopics(
            props.currentSurvey.template.id,
            props.currentSurvey.isTest ? props.currentSurvey.id : null, //SurveyId
            true,
            props.dashboardFilters
          )
        )

        //Get topics object of response
        if (!response.error) {
          response = response.topics
        }
      }
      //Get data for the survey from the server
      else {
        const topicsRequest = store.dispatch(
          fetchTopics(
            props.dashboardFilters,
            props._session.accountOptions.topicRepartitionCeil,
            false,
            props.currentSurvey.id,
            props.currentSurvey.dateStart,
            props.isReport ?? false
          )
        )

        noteRequest = store.dispatch(
          fetchNote(
            props.dashboardFilters,
            props._session.dashboardDisplaySettings.dashboardDisplayMode === "satisfaction",
            props.currentSurvey.id,
            props.currentSurvey.dateStart,
            undefined,
            props.isReport ?? false
          )
        )

        if (!props.isPreview) {
          axesRequest = store.dispatch(
            fetchAxes(
              props.dashboardFilters,
              props._session.userInitialLanguage,
              props._session.dashboardDisplaySettings.dashboardDisplayMode === "satisfaction",
              props.currentSurvey.id,
              props.currentSurvey.dateStart,
              props.isReport ?? false
            )
          )
        }

        //Si les thèmes sont trop peu nombreux pour la rosace, ces deux réponses ne sont pas utilisées : un échec ne doit
        //pas remonter comme une erreur non gérée
        noteRequest?.catch?.(() => undefined)
        axesRequest?.catch?.(() => undefined)

        response = await topicsRequest
      }

      if (isCancelled) return

      if (response.error === INSUFFICIENT_PARTICIPATION) {
        setStatus(INSUFFICIENT_PARTICIPATION)
      } else if (!response.error) {
        //Init new topics for survey topics
        //And update note for topics
        const newTopics: Topic[] = Array.from(props.currentTopics, (topic: Topic) => getArchiveTopic(topic, response))

        //Filter topic with score
        let noteTopics: Topic[] = newTopics.filter((x) => x.withScore && !x.hide)

        //Topics uniq by id
        if (props.currentSurvey.randomData) {
          noteTopics = uniqBy(noteTopics, "labelFull")
        }

        //If count of topics is below limit, search for propositions
        if (noteTopics.length >= DISPLAY_TREE_CEIL) {
          //Set note mean
          let noteValue = 0
          if (!props.currentSurvey.randomData) {
            const noteResponse: any = await noteRequest
            noteValue = noteResponse.error ? 0 : noteResponse.note
          } else {
            const notes = noteTopics.map((x) => x.note)
            noteValue = mean(notes)
          }

          if (isCancelled) return
          setNote(noteValue)

          //Save topics in state
          const orderedTopics = orderBy(
            noteTopics.map((x) => new Topic(x)),
            ["Axis.label", "Axis.id", props.isSortByName ? "label" : "note"],
            ["asc", "desc"]
          )
          setTopics(orderedTopics)

          //Axes (demandés en même temps que les thèmes)
          const axesResponse: any =
            props.currentSurvey.randomData || props.isPreview ? [] : await axesRequest

          if (isCancelled) return

          //Get axes From Topics
          if (!axesResponse.error) {
            //Update aid for axesResponse
            axesResponse.forEach((item) => {
              item.aid = axisFindTopicAid(item.aid)
            })

            //Update axes with note (calculte from topic list)
            const newAxes: Axis[] = Array.from(props.currentAxes, (axis: Axis) =>
              formatAxisResults(axis, newTopics, axesResponse, props._session.accountId, props.currentSurvey.randomData)
            )

            //Update axis list
            const finalAxes = newAxes.map((x: Axis) => new Axis(x))
            setAxes(finalAxes)
          } else {
            setAxes([])
          }
        } else {
          //Search propositions
          let propositions: any = []
          if (props.currentSurvey.randomData) {
            const generatedTopics: any = await store.dispatch(
              generateTopics(
                props.currentSurvey.template.id,
                props.currentSurvey.isTest ? props.currentSurvey.id : null, //SurveyId,
                false,
                props.dashboardFilters
              )
            )

            if (!generatedTopics.error) {
              propositions = generatedTopics.propositions
            }
          } else {
            propositions = await store.dispatch(
              fetchPropositions(props.dashboardFilters, props.currentSurvey.id, props.currentSurvey.dateStart)
            )
          }

          if (!propositions.error) {
            //Get responses most voted
            propositions = sortBy(propositions, "count").reverse().slice(0, 3)

            //Get questions for theses propositions
            let questions: Question[] = flatten(
              props.currentTopics.map((x: Topic) =>
                x.Questions.map(
                  (y: Question) =>
                    new Question({
                      ...y,
                      fk_topic: x.aid
                    })
                )
              )
            )
            questions = uniqBy(
              questions.filter(
                (x) => propositions.map((y) => y.fkQuestion).indexOf(props.currentSurvey.randomData ? x.id : x.aid) > -1
              ),
              "aid"
            )

            //Edit propositions for questions
            questions.forEach((question) => {
              question.Propositions.forEach((proposition) => {
                const archiveProposition = propositions.find(
                  (x) =>
                    x.aid === (props.currentSurvey.randomData ? proposition.id : proposition.aid) &&
                    x.fkQuestion === (props.currentSurvey.randomData ? question.id : question.aid)
                )

                if (archiveProposition) {
                  proposition.count = archiveProposition.count
                } else {
                  proposition.count = 0
                }
              })
            })

            //Update store for questions
            store.dispatch(questionGet(questions))
          }

          setTopics([])
        }

        //Set widget is loaded
        setStatus(STATUS_LOADED)
      } else {
        setTopics([])
        setStatus(STATUS_LOAD_ERROR)
      }
    }

    load()

    return () => {
      isCancelled = true
    }

    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dashboardFilters, props.currentSurvey.id, props.currentSurvey.randomData])

  //Click on axis : scroll at the axis in the page questions
  function clickAxis(axis) {
    if (!props.isPreview) {
      navigate("/dashboard/questions?axis_id=" + axis.id)
    }
  }

  //Click on a question
  //Scroll to topic of the question in the page question
  function clickQuestion(question) {
    if (!props.isPreview) {
      navigate("/dashboard/questions?topic_id=" + question.QuestionsTopic.TopicId)
    }
  }

  return (
    <Card
      className="flex1"
      exportName={props.exportName}
      helpSection="rosace"
      isWithoutPadding={props.isPreview || (status !== STATUS_LOADING && topics.length < DISPLAY_TREE_CEIL)}
      status={status}
      isBreathing={status === STATUS_LOADING}
      skeleton={
        <div style={{ width: props.isPreview ? 350 : WIDGET_WIDTH }}>
          <div className="flex">
            <Space />
            <DashboardResultsTopicsSkeletonWidget />
            <Space />
          </div>
        </div>
      }>
      <div
        style={{
          width: props.isPreview ? 350 : WIDGET_WIDTH
        }}>
        {status === INSUFFICIENT_PARTICIPATION ||
        (!props.currentSurvey.randomData &&
          props.isPreview &&
          props.currentSurvey.getParticipationCount(
            props._session.dashboardDisplaySettings.hideStartedForParticipation
          ) < props._session.participationMin) ? (
          <div className="flex1 flex flex-dcol">
            <div className="height-20" />
            <NoResultsWidget />
          </div>
        ) : (
          <div>
            <div
              //Shown on hover of the card like the hint of the guide (always shown on a touch screen : no hover there)
              className="flex abs opacity-0 transition-opacity group-hover:opacity-100 has-[:focus-visible]:opacity-100 [@media(hover:none)]:opacity-100"
              style={{
                top: 0,
                right: hasHelpHint ? 35 + HELP_COPY_BUTTON_WIDTH : 10, //Room for the hint of the guide and its copy button : same gap between the icons (10 px)
                zIndex: 4
              }}>
              {!props.isPreview &&
                modes.map((x: any) => (
                  <Tooltip key={x.name} text={t(x.label)} position="left" usePortal>
                    <FontAwesomeIcon
                      icon={x.icon}
                      color={mode === x.name ? props._session.accountColors.active : "#8C8C8C"}
                      size={"lg"}
                      className={mode === x.name ? undefined : "_hover"}
                      onClick={mode === x.name ? undefined : () => setMode(x.name)}
                      style={{
                        margin: "10px 5px"
                      }}
                    />
                  </Tooltip>
                ))}
            </div>

            {mode === "topics" && (
              <div>
                {
                  /* display tree if 6 word min */
                  topics.length >= DISPLAY_TREE_CEIL ? (
                    <div>
                      <div className="flex">
                        <Space />
                        <DashboardResultsTopicsWidget currentTopics={topics} note={note} isPreview={props.isPreview} />
                        <Space />
                      </div>

                      {!props.isPreview && <p className="grey-t">{t("dashboard_axes")}</p>}

                      <div style={props.isPreview ? { marginLeft: 42, width: 350 } : undefined}>
                        <DashboardAxesOverviewWidget axes={axes} onClick={(axis: Axis) => clickAxis(axis)} />
                      </div>
                    </div>
                  ) : (
                    //Else display questions with the more responses
                    <div>
                      <ListItem>
                        <b>{t("dashboard_results_propositions")}</b>
                      </ListItem>

                      {props.question.list.map((question) => (
                        <ListItem key={question.id} isEditable onClick={() => clickQuestion(question)}>
                          <div className="flex1">
                            <div className="grey-t">{question.label}</div>

                            {question.Propositions.filter((x) => x.count > 0).map((proposition) => (
                              <div key={proposition.id} className="flex">
                                <FontAwesomeIcon
                                  icon={faChevronRight}
                                  style={{
                                    marginTop: "2px",
                                    marginRight: "4px"
                                  }}
                                />
                                {proposition.label}
                                <Space />
                                <div className="width-20" />
                                {proposition.count} {proposition.count > 1 ? t("propositions") : t("proposition")}&nbsp;
                                (
                                {proposition.getRatio(
                                  props.currentSurvey.getParticipationCount(
                                    props._session.dashboardDisplaySettings.hideStartedForParticipation
                                  )
                                )}
                                %)
                              </div>
                            ))}
                          </div>
                        </ListItem>
                      ))}
                    </div>
                  )
                }
              </div>
            )}

            {mode === "axes" && (
              <div>
                <DashboardResultsAxesWidget
                  currentAxes={axes}
                  onClick={(axisId: string | null) =>
                    setSelectedAxis(
                      axisId
                        ? selectedAxis && selectedAxis.id === axisId
                          ? null
                          : axes.find((x: Axis) => x.id === axisId)!
                        : null
                    )
                  }
                />

                <div className="flex">
                  <Space />

                  <div>
                    <p className="grey-t">{t("dashboard_topics")}</p>

                    <div style={{ fontSize: 12, padding: "0 10px" }}>
                      {selectedAxis ? (
                        <div className="flex">
                          {selectedAxis!.label}
                          <Space />
                          <u className="_hover grey-t" onClick={() => setSelectedAxis(null)}>
                            {t("populations_reset")}
                          </u>
                        </div>
                      ) : (
                        <i>{t("filter_by_axis")}</i>
                      )}
                    </div>

                    <div className="height-10" />

                    <p className="grey-t">{t("rank_top", { number: 3 })}</p>

                    <DashboardTopicsPodiumWidget
                      currentTopics={topics}
                      selectedAxisId={selectedAxis ? selectedAxis!.id : undefined}
                      nbToDisplay={3}
                    />

                    <p className="grey-t">{t("rank_worst", { number: 3 })}</p>

                    <DashboardTopicsPodiumWidget
                      currentTopics={topics}
                      selectedAxisId={selectedAxis ? selectedAxis!.id : undefined}
                      reverse={true}
                      nbToDisplay={3}
                    />
                  </div>

                  <Space />
                </div>

                <div className="height-10" />

                <div className="flex">
                  <Space />
                  <u
                    className="_hover grey-t"
                    onClick={() =>
                      navigate(
                        selectedAxis ? "/dashboard/questions?axis_id=" + selectedAxis!.id : "/dashboard/questions"
                      )
                    }>
                    {t("utils_show")}
                  </u>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </Card>
  )
}

const mapStateToProps = (state) => ({
  _session: state._session,
  question: state.question
})

export default connect(mapStateToProps)(withTranslation()(DashboardResultsWidget))
